import { useContext, useEffect, useState } from 'react';
import { PageTitleContext } from 'context/PageTitleContext';
import { CrumbsContext } from 'context/CrumbsContext';
import { ApiConsumer } from 'api/ApiConsumer';
import { API_ROUTES } from 'routes/api';
import { CogIcon, DocumentDownloadIcon, SearchIcon } from '@heroicons/react/outline';
import { MusicChart } from 'components/Music/chart';
import ChartFilter from 'components/Reports/ChartFilter';

export default function Charts() {
  const [details, setDetails] = useState({
    chart_order: 'none',
    report: 'Music Charts',
  });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  let { setTitle } = useContext(PageTitleContext);
  let { setCrumbs } = useContext(CrumbsContext);
  const [downloadingReport, setDownloadingReport] = useState(false);


  function getUrl() {
    if (details?.chart_order === 'none')
      return API_ROUTES.BROADCAST.REPORTS.CHARTS;
    if (details?.chart_order === 'artist')
      return API_ROUTES.BROADCAST.REPORTS.TOP_ARTISTS;
    if (details?.chart_order === 'label')
      return API_ROUTES.BROADCAST.REPORTS.TOP_LABELS;
  }

  function searchReport() {
    setLoading(true);
    ApiConsumer.get(getUrl(), {
      params: { ...details, page: page },
      paramSerializer: function (params) {
        return JSON.stringify(params);
      },
    })
      .then((res) => {
        setResults(res.data);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    setLoading(true);
    const currentDate = new Date();
    const endDate = new Date(currentDate);
    endDate.setHours(23, 59, 59, 999); // Set to end of day

    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 30); // 30 days back
    startDate.setHours(0, 0, 0, 0); // Set to start of day

    setLoading(true);
    ApiConsumer.get(getUrl(), {
      params: { ...details, start_date: startDate, end_date: endDate, page: 1 },
      paramSerializer: function (params) {
        return JSON.stringify(params);
      },
    })
    .then((res) => {
      setResults(res.data);
    })
    .catch((err) => console.error(err))
    .finally(() => setLoading(false));
  }, []);

  function downloadReport() {
    setDownloadingReport(true)
    ApiConsumer.get(API_ROUTES.BROADCAST.REPORTS.CHARTS_DOWNLOAD, {
      params: { ...details, page: page },
      paramSerializer: function (params) {
        return JSON.stringify(params);
      },
      responseType: 'blob',
    })
      .then(({ data }) => downloadExcelDocument('Music Charts.xlsx', data))
      .catch((err) => console.error(err))
      .finally(() => setDownloadingReport(false));
  }

  const downloadExcelDocument = (name, data) => {
    const downloadUrl = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', name);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  useEffect(() => {
    setTitle('Music Charts');
    setCrumbs([
      { name: 'Charts & Reports', link: '/reports/charts' },
      {
        name: 'Music Charts',
        link: '/reports/charts',
      },
    ]);
  }, []);

  return (
    <>
      <div className="min-h-full px-5 py-5 mt-0 mb-24">
        <ChartFilter
          details={details}
          setDetails={setDetails}
          additional_filters={true}
        />
        <h3 className="mb-2 font-semibold profile-overview-title mt-5 flex mx-full">
          <span className="min-w-0 flex-1 items-center">
            Results (
            {details?.chart_type <= 100 ? details?.chart_type : results.length})
          </span>
          <SearchIcon
            className="h-8 w-8 pt-1.5 cursor-pointer hover:text-green-500"
            aria-hidden="true"
            onClick={searchReport}
          />
          { downloadingReport ? (
            <CogIcon className="animate-spin h-8 w-8 pt-1.5 cursor-pointer text-sabc-blue" />
          ) : 
            <DocumentDownloadIcon
              className="h-8 w-8 pt-1.5 cursor-pointer hover:text-green-500"
              aria-hidden="true"
              onClick={downloadReport}
            />
          }
        </h3>
        {details?.chart_order === 'none' ? (
          <MusicChart
            list={results}
            limit={details?.chart_type ?? 100}
            loading={loading}
            chart={true}
            include_count={true}
          />
        ) : null}
        {details?.chart_order === 'artist' ? (
          <MusicChart
            list={results}
            limit={details?.chart_type ?? 100}
            loading={loading}
            chart={true}
            include_count={true}
            ordered_chart={true}
            report={true}
          />
        ) : null}
        {details?.chart_order === 'label' ? (
          <MusicChart
            list={results}
            limit={details?.chart_type ?? 100}
            loading={loading}
            chart={true}
            include_count={true}
            ordered_chart={true}
            report={true}
          />
        ) : null}
      </div>
    </>
  );
}
